/**
 * Aparência do launcher, no lado do loader.
 *
 * Módulo PURO — nenhuma referência a `document`. Existe separado porque o
 * `loader/index.ts` é um script que se autoexecuta (`main()` no
 * `DOMContentLoaded`) e não exportava nada: sem extrair, não havia como testar
 * a lógica que decide o que o visitante vê. E este é o código do repositório
 * com o pior custo de regressão — ele roda no site dos clientes.
 *
 * As chaves de entrada são **camelCase**, que é a convenção da API inteira. Vale
 * a pena registrar por que isto já esteve errado: o loader chama `fetch` direto
 * em `/api/public/widget/config`, sem passar pelo `lib/api/http.js` do app, e eu
 * supus que o `http.js` gravava em snake_case. Ele converte o corpo de saída com
 * `toCamel` (`http.js:139`), então o json guardado tem `launcherModel`, e o
 * loader lia `launcher_model` — TODO campo caía no default e nada do que o
 * cliente escolhia aparecia no site dele.
 *
 * `src/components/settings/widget/chat-tab/appearanceContract.test.js` é a
 * guarda dessa fronteira: parte do formulário do painel e exige que o que sai
 * de lá seja lido aqui.
 */

export const LAUNCHER_MODELS = ['circular', 'avatar', 'barra', 'destacado'] as const;

export type LauncherModel = (typeof LAUNCHER_MODELS)[number];

export interface LauncherAppearance {
  showLauncher: boolean;
  model: LauncherModel;
  pulse: boolean;
  showAvatar: boolean;
  showClose: boolean;
  backgroundStyle: 'solid' | 'gradient';
  /** `null` significa "usar a `primaryColor` da branding". */
  buttonColor: string | null;
  textColor: string;
  mainText: string;
  callToAction: string;
  quickQuestions: string[];
  windowTheme: 'light' | 'dark';
  windowBackgroundStyle: 'solid' | 'gradient';
  windowColor: string | null;
}

export interface LegacyLauncherBranding {
  launcherMessage?: string | null;
  launcherTextColor?: 'white' | 'black' | null;
}

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function asText(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.trim();
}

function legacyTextColor(value: LegacyLauncherBranding['launcherTextColor']): string | null {
  if (value === 'white') return '#FFFFFF';
  if (value === 'black') return '#0F172A';
  return null;
}

/**
 * Só hexadecimal de 6 dígitos.
 *
 * O valor vai para `style.backgroundColor`. Texto arbitrário ali é simplesmente
 * ignorado pelo navegador, e o resultado é um botão transparente no site do
 * cliente — falha visual sem mensagem de erro.
 */
function asColor(value: unknown): string | null {
  return typeof value === 'string' && HEX_COLOR.test(value) ? value : null;
}

function asOption<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

/**
 * Quais propriedades cada modelo realmente usa.
 *
 * Metade vanilla da guarda de paridade: `loader/launcher-parity.test.ts` compara
 * este mapa com o `launcherControls` do React, para que o preview do painel e o
 * launcher do site não divirjam sobre qual modelo tem avatar, texto ou acesso
 * rápido — divergência que ninguém vê até um cliente reclamar que o que ele
 * configurou não é o que apareceu.
 *
 * A duplicação com o React é deliberada: o loader é um script independente
 * servido a sites de terceiros, e importar código do app o acoplaria ao bundle
 * do painel. O custo dessa escolha é esta guarda.
 */
export function launcherModelProperties(model: LauncherModel) {
  switch (model) {
    case 'avatar':
      return { usesAvatar: true, usesText: false, usesClose: false, usesQuickQuestions: false };
    case 'barra':
      return { usesAvatar: true, usesText: true, usesClose: true, usesQuickQuestions: false };
    case 'destacado':
      return { usesAvatar: true, usesText: true, usesClose: true, usesQuickQuestions: true };
    default:
      // circular: ícone de conversa, sem avatar e sem texto onde aplicar cor.
      return { usesAvatar: false, usesText: false, usesClose: false, usesQuickQuestions: false };
  }
}

export interface LauncherMetrics {
  /** Largura mínima da cápsula, em px. Nos redondos é o diâmetro. */
  minWidth: number;
  /** Altura mínima da cápsula, em px. Nos redondos é o diâmetro. */
  minHeight: number;
  /** Lado do avatar, em px. Ignorado pelos modelos que não usam avatar. */
  avatarSize: number;
  /** Largura do painel de acesso rápido, em px. Só o `destacado` desenha. */
  panelWidth: number;
}

/**
 * O tamanho de cada modelo.
 *
 * Existe porque `barra` e `destacado` desenhavam a MESMA cápsula, dimensionada
 * pelo conteúdo: a barra saía estreita como uma pílula, e o destacado não tinha
 * nada que o destacasse além do painel acima. Agora a barra tem largura de barra
 * e o destacado tem altura e avatar maiores.
 *
 * Os números batem com o painel de acesso rápido (`panelWidth`) de propósito: no
 * `destacado` a cápsula e o painel formam um bloco só, e larguras diferentes
 * fariam um degrau visível.
 *
 * A outra metade destes números vive em `launcherMetrics`, no painel, e
 * `loader/launcher-parity.test.ts` exige que sejam iguais — preview que mente
 * sobre o tamanho é tão ruim quanto preview que mente sobre a cor.
 */
export function launcherModelMetrics(model: LauncherModel): LauncherMetrics {
  switch (model) {
    case 'avatar':
      return { minWidth: 56, minHeight: 56, avatarSize: 56, panelWidth: 260 };
    case 'barra':
      return { minWidth: 260, minHeight: 56, avatarSize: 40, panelWidth: 260 };
    case 'destacado':
      return { minWidth: 260, minHeight: 76, avatarSize: 52, panelWidth: 260 };
    default:
      // circular: o ícone de conversa num botão redondo.
      return { minWidth: 56, minHeight: 56, avatarSize: 56, panelWidth: 260 };
  }
}

/** A versão compacta temporária usada depois de o visitante fechar a barra. */
export function collapseLauncherToAvatar(appearance: LauncherAppearance): LauncherAppearance {
  return {
    ...appearance,
    model: 'avatar',
    showAvatar: true,
    showClose: false,
    quickQuestions: [],
  };
}

/**
 * Sempre devolve uma aparência — NUNCA `null`.
 *
 * A feature é ligada por padrão: todo cliente recebe o launcher novo, mesmo sem
 * nunca ter aberto a configuração. Entrada ausente, vazia ou inválida cai nos
 * mesmos defaults, e isso importa: se divergissem, o mesmo cliente veria
 * launchers diferentes dependendo de a coluna estar `NULL` ou conter um json que
 * este loader não entendeu.
 *
 * Consequência que a decisão assume: o launcher legado (círculo com bolha de
 * fala exibindo `launcherMessage`) deixa de ser desenhado em qualquer cenário. A
 * bolha não tem equivalente no modelo `circular`, que é o default do design.
 */
export function normalizeAppearance(
  raw: Record<string, unknown> | null | undefined,
  legacyBranding: LegacyLauncherBranding = {}
): LauncherAppearance {
  const original = raw && isPlainObject(raw) ? raw : {};
  const dados: Record<string, unknown> = {};

  // O cliente autenticado converte o payload inteiro para snake_case antes de
  // salvar, inclusive as chaves desta coluna JSON. Já o widget público consome
  // o endpoint com fetch direto e recebe esse JSON sem a conversão inversa.
  // Normalizar aqui mantém compatibilidade com registros antigos nos dois
  // formatos; quando ambos existirem, a chave camelCase canônica vence.
  for (const [key, value] of Object.entries(original)) {
    const camelKey = key.replace(/_([a-z])/g, (_, char: string) => char.toUpperCase());
    dados[camelKey] = value;
  }
  Object.assign(dados, original);

  const quickQuestions = Array.isArray(dados.quickQuestions)
    ? dados.quickQuestions
        .filter((q): q is string => typeof q === 'string')
        .map((q) => q.trim())
        .filter(Boolean)
    : [];

  const mainText = asText(dados.mainText);
  const callToAction = asText(dados.callToAction);
  // Widgets criados antes dos campos novos guardam apenas `launcherMessage`.
  // Mostre-o como chamada quando os dois textos novos estiverem vazios, sem
  // sobrescrever uma personalização atual.
  const legacyMessage = !mainText && !callToAction ? asText(legacyBranding.launcherMessage) : '';

  return {
    // O launcher aparece por padrão: widget configurado que não desenha nada é
    // indistinguível de widget quebrado.
    showLauncher: asBoolean(dados.showLauncher, true),
    model: asOption(dados.launcherModel, LAUNCHER_MODELS, 'circular'),
    // Pulso não vem por padrão — é animação no site de terceiro.
    pulse: asBoolean(dados.pulse, false),
    showAvatar: asBoolean(dados.showAvatar, true),
    showClose: asBoolean(dados.showClose, true),
    backgroundStyle: asOption(dados.buttonBackgroundStyle, ['solid', 'gradient'], 'solid'),
    // Não vem no json: a cor do botão é a coluna `primary_color`, servida na
    // branding como `primaryColor`. Fica aqui para o tipo ser completo, sempre
    // nulo — quem pinta o launcher usa a branding.
    buttonColor: asColor(dados.buttonColor),
    textColor:
      asColor(dados.buttonTextColor) ??
      legacyTextColor(legacyBranding.launcherTextColor) ??
      '#FFFFFF',
    mainText,
    callToAction: callToAction || legacyMessage,
    quickQuestions,
    windowTheme: asOption(dados.windowTheme, ['light', 'dark'], 'light'),
    windowBackgroundStyle: asOption(dados.windowBackgroundStyle, ['solid', 'gradient'], 'solid'),
    windowColor: asColor(dados.windowColor),
  };
}
